<template>
  <div class="el-main">
    <!--差出人情報-->
    <div class="card" v-show="currentCardId == 'userprofile'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>差出人情報</h2>
          <p>
            登録情報と異なる場合は修正しましょう<br />宛名ラベルの差出人欄に印刷されます
          </p>
        </div>

        <div class="card-content">
          <div class="edit-form">
            <div class="edit-form-item">
              <div class="item-name">氏名</div>
              <div class="item-body flex">
                <el-input
                  v-model="formatData.lastName"
                  class="input-sm"
                  placeholder=""
                  title="姓"
                  @keyup.native.enter="formLib.nextInput(firstNameInput)"
                ></el-input>
                <el-input
                  v-model="formatData.firstName"
                  ref="firstNameInput"
                  class="input-sm"
                  placeholder=""
                  title="名"
                ></el-input>
              </div>
            </div>
            <div class="edit-form-item">
              <div class="item-name">
                郵便番号
                <small v-if="deviceLib.isOnline">(ハイフン不要)</small>
              </div>
              <div class="item-body">
                <el-input
                  class="input-7-digit"
                  placeholder="1001000"
                  title="郵便番号"
                  type="tel"
                  v-model.lazy="formatData.postNumber"
                  v-on:blur="
                    formatData.postNumber = cleanUpPostNumber(
                      formatData.postNumber
                    )
                  "
                ></el-input>
              </div>
              <div class="item-name">都道府県・市区町村・番地</div>
              <div class="item-body">
                <el-input
                  type="textarea"
                  autosize
                  placeholder=""
                  title="都道府県・市区町村"
                  v-model="formatData.address1"
                  v-on:blur="
                    formatData.address1 = formLib.zenToHanNumber(
                      formatData.address1
                    )
                  "
                ></el-input>
              </div>
              <div class="item-name">マンション・建物名等</div>
              <div class="item-body">
                <el-input
                  type="textarea"
                  autosize
                  placeholder=""
                  title="マンション・建物名等"
                  v-model="formatData.address2"
                  v-on:blur="
                    formatData.address2 = formLib.zenToHanNumber(
                      formatData.address2
                    )
                  "
                ></el-input>
              </div>
              <div
                v-if="senderAddressLength > 0"
                class="atena-char-count"
                :class="{ 'is-over': isSenderAddressOver }"
              >
                住所合計 {{ senderAddressLength }}/{{ charLimits.address }}文字
                <span v-if="isSenderAddressOver"
                  >— 文字数が多いため印刷時にはみ出る可能性があります</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--サイズ-->
    <div class="card" v-show="currentCardId == 'size'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>サイズ選択</h2>
          <p>サイズごとに最大件数が異なるので、ご注意ください</p>
        </div>
        <div class="card-content">
          <atena-paper-select-card
            :selected-paper="formatData.selectedPaper"
            @select="selectOption"
          />
        </div>
      </div>
    </div>

    <!--宛名情報-->
    <div class="card" v-show="currentCardId == 'atena-info'">
      <div class="overflow-scroll-y">
        <div class="card-header">
          <h2>宛名情報</h2>
          <p>送りたい企業の情報を入力しましょう</p>
        </div>

        <div class="card-content-list">
          <atena-items-card
            :atena-items="formatData.atenaItems"
            :max-atena-items="maxAtenaItems"
            :char-limits="charLimits"
            @add="addAtenaItem"
            @delete="deleteAtenaItem"
          />
        </div>
      </div>
    </div>

    <!--確認-->
    <div class="card" v-show="currentCardId == 'confirm'">
      <div class="overflow-scroll-y">
        <!-- 旧 margin-bottom:-3rem はh2のみだった頃の詰め。切替ボタン追加後は後続のフォント選択と重なるため 0 に -->
        <div class="card-header" style="max-width: 40rem; margin: 2rem auto 0">
          <h2>宛名ラベルを確認</h2>
          <!-- 確認しながらサイズを切り替えられるようにする（選択はサイズ選択カードと同じ formatData.selectedPaper） -->
          <div class="confirm-paper-switch">
            <button
              v-for="option in paperOptions"
              :key="option.id"
              type="button"
              class="confirm-paper-switch__btn"
              :class="{ 'is-active': formatData.selectedPaper === option.id }"
              @click="selectOption(option.id)"
            >
              {{ option.title }}
            </button>
          </div>
          <p v-if="selectedPaperOption" class="confirm-paper-switch__store">
            {{ selectedPaperOption.storeNote }}
          </p>
          <p
            v-if="formatData.atenaItems.length > maxAtenaItems"
            class="confirm-paper-switch__note"
          >
            ※選択中の用紙には宛先{{ maxAtenaItems }}件まで印刷されます。{{
              maxAtenaItems + 1
            }}件目以降は印刷されません
          </p>
        </div>
      </div>
    </div>

    <!--完了ページ-->
    <div
      class="card"
      v-show="currentCardId == 'output'"
      v-if="mode != 'production1'"
    >
      <CompletedOutputPanel
        :formatId="formatId"
        :isSendYagiEmailEnabled="true"
        :mailPdfEnabled="mailPdfEnabled"
        :downloadPdfEnabled="downloadPdfEnabled"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { emitter } from '@/plugins/emitter'
import { useFormatLocalData } from '@/composables/format-edit/useFormatLocalData'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import AtenaItemsCard from '@/components/formats/AtenaItemsCard.vue'
import AtenaPaperSelectCard from '@/components/formats/AtenaPaperSelectCard.vue'
import CompletedOutputPanel from '@/components/format-edit/completed/CompletedOutputPanel.vue'
import {
  ATENA_CHAR_LIMITS,
  ATENA_MAX_ITEMS,
  createAtenaItem,
  createEmptyAtenaFormatData,
  isAtenaLSize,
  normalizeAtenaFormatData,
  type AtenaFormatData
} from '@/utils/format-edit/atenaFormat'
import paperOptions from '@/utils/format-edit/atenaPaperOptions.json'

defineProps<{
  basicData?: Record<string, unknown>
  formatId?: string
  // AtenaPreview のコンポーネントインスタンス（FormatEdit の $refs.previewFormat）
  previewFormat?: { $el?: HTMLElement } | null
  currentCardId?: string
  exampleSentences?: Record<string, unknown>
}>()

const { formLib, deviceLib } = useHostServices()

const mode = process.env.VUE_APP_MODE

const { formatData } = useFormatLocalData<AtenaFormatData>({
  saveName: 'atena',
  createEmpty: createEmptyAtenaFormatData
})

// ロード（初回・backup-loaded 再ロード）後の正規化。
// 正常値には何もしないため deep watcher で常時かけても収束する
watch(
  formatData,
  () => {
    normalizeAtenaFormatData(formatData)
    if (!formatData.createDate) {
      formatData.createDate = formLib.getToday()
    }
  },
  { deep: true, immediate: true }
)

const firstNameInput = ref(null)

const isLSize = computed(() => isAtenaLSize(formatData.selectedPaper))

// 確認画面でも切替先の対応コンビニを出す（L版はセブン非対応）
const selectedPaperOption = computed(() =>
  paperOptions.find((option) => option.id === formatData.selectedPaper)
)

// 宛名の最大入力件数。L判はシール紙1枚(宛先2面+差出人2面)のため2件まで
const maxAtenaItems = computed(() =>
  isLSize.value ? ATENA_MAX_ITEMS.lsize : ATENA_MAX_ITEMS.portrait
)

// AtenaPreview と同じ上限値を共有（atenaFormat.ts が単一の正）
const charLimits = computed(() =>
  isLSize.value ? ATENA_CHAR_LIMITS.lsize : ATENA_CHAR_LIMITS.portrait
)

const senderAddressText = computed(() =>
  [formatData.address1, formatData.address2].filter(Boolean).join('\n')
)
const senderAddressLength = computed(() => senderAddressText.value.length)
const isSenderAddressOver = computed(
  () => senderAddressLength.value > charLimits.value.address
)

function cleanUpPostNumber(value: string | undefined) {
  if (!value) return ''
  return String(formLib.zenToHanNumber(value.replace(/-/g, '')))
}

function selectOption(id: string) {
  formatData.selectedPaper = id
}

function addAtenaItem() {
  if (formatData.atenaItems.length >= maxAtenaItems.value) {
    return
  }
  formatData.atenaItems.push(createAtenaItem())
}

function deleteAtenaItem(index: number) {
  formatData.atenaItems.splice(index, 1)
}

// PDFメール・ダウンロードボタンの再有効化（完了画面パネルが送信中に無効化する既存挙動の維持）
const mailPdfEnabled = ref(true)
const downloadPdfEnabled = ref(true)

function dataChanged() {
  mailPdfEnabled.value = true
  downloadPdfEnabled.value = true
}

emitter.on('basic-data-changed', dataChanged)
emitter.on('format-data-changed', dataChanged)

onBeforeUnmount(() => {
  emitter.off('basic-data-changed', dataChanged)
  emitter.off('format-data-changed', dataChanged)
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables';

.confirm-paper-switch {
  display: flex;
  justify-content: center;
  margin-top: 12px;

  &__btn {
    padding: 6px 14px;

    & + & {
      margin-left: 8px;
    }

    font-size: 12px;
    color: $maincolor;
    background: $white;
    border: 1px solid $maincolor;
    border-radius: 16px;
    cursor: pointer;

    &.is-active {
      color: $white;
      background: $maincolor;
    }
  }

  &__store {
    margin-top: 6px;
    text-align: center;
    font-size: 11px;
    color: $font-dark;
  }

  &__note {
    margin-top: 6px;
    text-align: center;
    font-size: 10px;
    color: $red;
  }
}

.atena-char-count {
  font-size: 11px;
  color: $gray;
  margin-top: 2px;
  padding-left: 2px;

  &.is-over {
    color: #e74c3c;
    font-weight: bold;
  }
}
</style>
